import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { inSupportSession } from '../utils/supportSession';
import { useImpersonation } from './ImpersonationContext';
import supportService from '../services/supportService';

const SupportContext = createContext(null);
const SUMMARY_REFRESH_MS = 60 * 1000;

export function SupportProvider({ children }) {
  const { currentUser, userRole, organization, companyView } = useAuth();
  const { isImpersonating } = useImpersonation();
  const hasDeveloperRole = ['developer', 'superuser'].includes(userRole);
  const developer = hasDeveloperRole && !isImpersonating;
  const enabled = Boolean(
    currentUser
    && !companyView
    && !inSupportSession() // AC1: the customer help inbox is not for Car Wash Minder support logins
    && (developer || organization?.id || organization?.orgId)
  );
  const [summary, setSummary] = useState({ unreadCount: 0, banner: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // One summary request in flight at a time. Overlapping silent refreshes
  // (interval + focus + visibility firing together) share the pending call.
  const inFlight = useRef(null);

  const refreshSummary = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) {
      setSummary({ unreadCount: 0, banner: null });
      setError(null);
      return;
    }
    if (inFlight.current) return inFlight.current;
    if (!silent) setLoading(true);
    try {
      inFlight.current = supportService.getSummary({ developer });
      const next = await inFlight.current;
      setSummary({
        unreadCount: Number(next?.unreadCount) || 0,
        // Developers use the queue badge; customer banners are intentionally
        // reserved for messages addressed to the signed-in customer.
        banner: developer ? null : next?.banner || null
      });
      setError(null);
    } catch (requestError) {
      if (requestError.response?.status !== 404) {
        setError('Support notifications could not be refreshed.');
      }
    } finally {
      inFlight.current = null;
      if (!silent) setLoading(false);
    }
  }, [developer, enabled]);

  useEffect(() => {
    setSummary({ unreadCount: 0, banner: null });
    refreshSummary();
  }, [currentUser?.uid, organization?.id, refreshSummary]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = window.setInterval(() => {
      // A hidden tab never polls. Chrome aligns hidden tabs' timers so every
      // forgotten dashboard tab fired this together, once a minute, all day
      // (2026-09-23 incident, item J). The visibilitychange handler below
      // refreshes the moment the tab is shown again.
      if (document.visibilityState === 'hidden') return;
      refreshSummary({ silent: true });
    }, SUMMARY_REFRESH_MS);
    const refreshOnFocus = () => refreshSummary({ silent: true });
    const refreshOnVisibility = () => {
      if (document.visibilityState === 'visible') refreshSummary({ silent: true });
    };
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
    };
  }, [enabled, refreshSummary]);

  const dismissBanner = useCallback(async () => {
    if (!summary.banner?.requestId || developer || isImpersonating) return;
    const dismissedRequestId = summary.banner.requestId;
    setSummary((current) => ({ ...current, banner: null }));
    try {
      await supportService.dismiss(dismissedRequestId);
      await refreshSummary({ silent: true });
    } catch (requestError) {
      setError('The support notification could not be dismissed.');
      await refreshSummary({ silent: true });
    }
  }, [developer, isImpersonating, refreshSummary, summary.banner]);

  const markRequestRead = useCallback(async (requestId) => {
    if (!requestId || developer || isImpersonating) return;
    await supportService.markRead(requestId);
    await refreshSummary({ silent: true });
  }, [developer, isImpersonating, refreshSummary]);

  const value = useMemo(() => ({
    enabled,
    developer,
    writesBlocked: isImpersonating,
    unreadCount: summary.unreadCount,
    banner: summary.banner,
    loading,
    error,
    dismissBanner,
    markRequestRead,
    refreshSummary
  }), [
    enabled,
    developer,
    isImpersonating,
    summary,
    loading,
    error,
    dismissBanner,
    markRequestRead,
    refreshSummary
  ]);

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
}

export function useSupport() {
  const context = useContext(SupportContext);
  if (!context) throw new Error('useSupport must be used inside SupportProvider');
  return context;
}

export { SUMMARY_REFRESH_MS };
